import React, { useState } from 'react'
import { Card, Button, Select, Input, Row, Col, Steps, message } from 'antd'
import {
  ArrowLeftOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined,
  PhoneOutlined,
  MessageOutlined,
  VideoCameraOutlined
} from '@ant-design/icons'
import { useSelector } from 'react-redux'
import { createBooking } from '../../Bookings/utils/utils'
import { executeBooking, registerBooking } from '../../Bookings/utils/bpm-utils'
import { generateOTP } from '../../lib/utils'
import dayjs from 'dayjs'
import EmbeddedPayment from '../../PeachPayments/components/EmbeddedPayment'

const { Option } = Select
const { TextArea } = Input
const { Step } = Steps

const FlexiPriorityBooking = ({ consultationType, onBack }) => {
  console.log('🎯 FlexiPriorityBooking component rendered with:', { consultationType })

  const [currentStep, setCurrentStep] = useState(0)
  const [loading, setLoading] = useState(false)
  const [showPayment, setShowPayment] = useState(false)
  const [paymentCompleted, setPaymentCompleted] = useState(false)
  const [paymentReference, setPaymentReference] = useState(null)
  const [bookingData, setBookingData] = useState({
    callType: '',
    reason: '',
    chiefComplaint: ''
  })

  // Get user and entity info from Redux
  const coreState = useSelector((state) => state.core)
  const authState = useSelector((state) => state.auth)

  const callTypes = [
    {
      value: 'phone',
      label: 'Voice Call',
      icon: <PhoneOutlined />,
      description: 'Audio consultation via phone call'
    },
    {
      value: 'video',
      label: 'Video Call',
      icon: <VideoCameraOutlined />,
      description: 'Face-to-face consultation via video'
    },
    {
      value: 'whatsapp',
      label: 'WhatsApp',
      icon: <MessageOutlined />,
      description: 'Video call via WhatsApp'
    }
  ]

  const appointmentReasons = [
    'General Consultation',
    'Follow-up Appointment',
    'Prescription Renewal',
    'Medical Certificate',
    'Test Results Discussion',
    'Chronic Disease Management',
    'Mental Health Support',
    'Second Opinion',
    'Urgent Medical Advice',
    'Other'
  ]

  const steps = [
    {
      title: 'Contact Method',
      description: "Choose how you'd like to be contacted"
    },
    {
      title: 'Consultation Details',
      description: 'Provide consultation information'
    },
    {
      title: 'Confirmation',
      description: 'Review and confirm your booking'
    }
  ]

  const handleNext = () => {
    if (currentStep === 0 && !bookingData.callType) {
      message.error('Please select a contact method')
      return
    }
    if (currentStep === 1 && (!bookingData.reason || !bookingData.chiefComplaint.trim())) {
      message.error('Please fill in all consultation details')
      return
    }
    setCurrentStep(currentStep + 1)
  }

  const handlePrevious = () => {
    setCurrentStep(currentStep - 1)
  }

  const handleInputChange = (field, value) => {
    setBookingData((prev) => ({
      ...prev,
      [field]: value
    }))
  }

  // Get consultation cost
  const getConsultationCost = () => {
    if (consultationType === 'flexi') return 10 // Demo amount
    if (consultationType === 'priority') return 101 // Demo amount
    return 0
  }

  // Handle payment completion
  const handlePaymentSuccess = (merchantReference) => {
    console.log('💰 FlexiPriority Payment completed successfully:', merchantReference)
    console.log('💰 Setting payment state: completed=true, reference=', merchantReference)

    setPaymentReference(merchantReference)
    setPaymentCompleted(true)
    setShowPayment(false)

    // Directly create booking instead of calling handleSubmit again
    console.log('💰 Proceeding directly to booking creation')
    createBookingDirectly(merchantReference)
  }

  // Create booking directly after payment success
  const createBookingDirectly = async (merchantReference) => {
    setLoading(true)
    try {
      console.log('🔍 FlexiPriority: Creating booking directly with payment reference:', merchantReference)

      // EXACTLY copy the pattern from working AppointmentConfirmationMobile
      let processRecord = null
      const bpmKey = 'patient_journey'
      const processUser = `${authState.agiliteUser.firstName} ${authState.agiliteUser.lastName}`

      // FIRST: Register booking
      processRecord = await registerBooking(bpmKey, processUser)

      // For virtual consultations, skip check-in and go directly to consultation
      let targetStep = 'consultation'

      // THEN: Execute booking
      processRecord = await executeBooking(
        bpmKey,
        processRecord.recordId,
        'submit',
        processUser,
        processRecord.key,
        targetStep
      )

      // Use today for flexi/priority
      const normalizedDate = dayjs().hour(2).minute(0).second(0).millisecond(0).toDate()
      const otp = generateOTP(6)

      // Prepare booking data
      const bookingDataObj = {
        userRef: authState.agiliteUser._id,
        otp,
        status: processRecord.key,
        bookingDate: normalizedDate,
        reason: bookingData.reason,
        chiefComplaint: bookingData.reason,
        processRef: processRecord.recordId,
        isVirtualVisit: true,
        isHomeVisit: false,
        serviceType: consultationType,
        callType: bookingData.callType,
        dischem: null,
        isPriorityConsult: consultationType === 'priority',
        consultType: consultationType,
        appointmentType: consultationType,
        patientEntityRef: coreState?.entity?._id,
        claimable: true,
        availableForClaiming: true,
        claimedBy: null,
        claimedAt: null,
        medicalProfRef: null,
        clinicRef: null,
        startTime: null,
        endTime: null,
        // Add payment reference
        callpayGatewayReference: merchantReference
      }

      console.log('🔍 FlexiPriority: Booking data:', bookingDataObj)

      const newBooking = await createBooking(bookingDataObj)
      console.log('🔍 FlexiPriority: Booking created successfully:', newBooking)

      // Success - move to step 3
      message.success(`${consultationType === 'flexi' ? 'Flexi' : 'Priority'} consultation booked successfully!`)
      setCurrentStep(3)
    } catch (error) {
      console.error('🔍 FlexiPriority: Error creating booking:', error)
      message.error('Failed to create booking. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async () => {
    // Check if payment is required and not completed
    const consultationCost = getConsultationCost()
    console.log('🔍 FlexiPriority handleSubmit called:', {
      consultationCost,
      paymentCompleted,
      paymentReference,
      shouldShowPayment: consultationCost > 0 && !paymentCompleted
    })

    if (consultationCost > 0 && !paymentCompleted) {
      console.log('💰 FlexiPriority: Payment required, showing payment screen')
      setShowPayment(true)
      return
    }

    // This should never be reached if payment is working correctly
    console.log('🚨 FlexiPriority: handleSubmit called but payment should have been completed!')
    console.log('🚨 Current state:', { paymentCompleted, paymentReference, consultationCost })

    // If we somehow get here without payment, something went wrong
    message.error('Payment verification failed. Please try the payment process again.')
    setShowPayment(true)
  }

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div>
            <h3 style={{ marginBottom: '16px' }}>How would you like to be contacted?</h3>
            <Row gutter={[16, 16]}>
              {callTypes.map((type) => (
                <Col xs={24} sm={8} key={type.value}>
                  <Card
                    hoverable
                    style={{
                      border: bookingData.callType === type.value ? '2px solid #1890ff' : '1px solid #d9d9d9',
                      cursor: 'pointer'
                    }}
                    bodyStyle={{ textAlign: 'center', padding: '16px' }}
                    onClick={() => handleInputChange('callType', type.value)}
                  >
                    <div style={{ fontSize: '24px', marginBottom: '8px' }}>{type.icon}</div>
                    <h4>{type.label}</h4>
                    <p style={{ fontSize: '12px', color: '#666' }}>{type.description}</p>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        )

      case 1:
        return (
          <div>
            <h3 style={{ marginBottom: '16px' }}>Consultation Details</h3>
            <Row gutter={[16, 16]}>
              <Col xs={24}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Reason for Visit *</label>
                <Select
                  placeholder='Select reason for consultation'
                  value={bookingData.reason}
                  onChange={(value) => handleInputChange('reason', value)}
                  style={{ width: '100%' }}
                  size='large'
                >
                  {appointmentReasons.map((reason) => (
                    <Option key={reason} value={reason}>
                      {reason}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Describe your symptoms or concerns *
                </label>
                <TextArea
                  placeholder='Please describe your symptoms, concerns, or the reason for this consultation...'
                  value={bookingData.chiefComplaint}
                  onChange={(e) => handleInputChange('chiefComplaint', e.target.value)}
                  rows={4}
                  maxLength={500}
                  showCount
                />
              </Col>
            </Row>
          </div>
        )

      case 2:
        return (
          <div>
            <h3 style={{ marginBottom: '16px' }}>Confirm Your Booking</h3>
            <Card style={{ marginBottom: '16px' }}>
              <Row gutter={[16, 16]}>
                <Col xs={24} sm={12}>
                  <p>
                    <strong>Consultation Type:</strong>
                  </p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {consultationType === 'flexi' ? (
                      <ClockCircleOutlined style={{ color: '#52c41a' }} />
                    ) : (
                      <ThunderboltOutlined style={{ color: '#ff4d4f' }} />
                    )}
                    <span style={{ textTransform: 'capitalize', fontWeight: '500' }}>
                      {consultationType} Consultation
                    </span>
                  </div>
                </Col>
                <Col xs={24} sm={12}>
                  <p>
                    <strong>Expected Wait Time:</strong>
                  </p>
                  <span>{consultationType === 'flexi' ? 'Within the day' : 'Within 15 minutes'}</span>
                </Col>
                <Col xs={24} sm={12}>
                  <p>
                    <strong>Contact Method:</strong>
                  </p>
                  <span>{callTypes.find((t) => t.value === bookingData.callType)?.label}</span>
                </Col>
                <Col xs={24} sm={12}>
                  <p>
                    <strong>Reason:</strong>
                  </p>
                  <span>{bookingData.reason}</span>
                </Col>
                <Col xs={24}>
                  <p>
                    <strong>Description:</strong>
                  </p>
                  <span>{bookingData.chiefComplaint}</span>
                </Col>
              </Row>
            </Card>
            <div
              style={{
                background: '#f6ffed',
                border: '1px solid #b7eb8f',
                borderRadius: '6px',
                padding: '12px',
                marginBottom: '16px'
              }}
            >
              <p style={{ margin: 0, fontSize: '14px' }}>
                <strong>What happens next?</strong>
                <br />
                {consultationType === 'flexi'
                  ? 'A doctor from our network will review your request and contact you sometime during the day when available.'
                  : 'A doctor will be notified immediately and will contact you within 15 minutes for urgent medical attention.'}
              </p>
            </div>
          </div>
        )

      case 3:
        return (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px', color: '#52c41a', marginBottom: '16px' }}>✓</div>
            <h2 style={{ color: '#52c41a', marginBottom: '16px' }}>Booking Confirmed!</h2>
            <p style={{ fontSize: '16px', marginBottom: '24px' }}>
              Your {consultationType} consultation has been successfully booked.
            </p>
            <Card style={{ marginBottom: '24px', textAlign: 'left' }}>
              <p>
                <strong>Booking Reference:</strong> #{Date.now()}
              </p>
              <p>
                <strong>Status:</strong> Waiting for doctor assignment
              </p>
              <p>
                <strong>Expected Contact:</strong>{' '}
                {consultationType === 'flexi' ? 'Within the day' : 'Within 15 minutes'}
              </p>
            </Card>
            <Button type='primary' size='large' onClick={() => (window.location.href = '/dashboard')}>
              Go to Dashboard
            </Button>
          </div>
        )

      default:
        return null
    }
  }

  // Show payment screen if payment is required (but not if we're on success step)
  if (showPayment && getConsultationCost() > 0 && currentStep < 3) {
    return (
      <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ marginBottom: '24px' }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => setShowPayment(false)} style={{ marginBottom: '16px' }}>
            Back to Booking
          </Button>
          <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>Complete Payment</h1>
          <p style={{ color: '#8c8c8c' }}>
            Pay R{getConsultationCost()} for your {consultationType} consultation
          </p>
        </div>

        <EmbeddedPayment
          patientRecord={coreState?.entity}
          amount={getConsultationCost()}
          paymentType='credit_card'
          isAuthorization={true}
          callback={(merchantReference) => {
            console.log('💰 FlexiPriority Payment completed, proceeding to booking creation')
            handlePaymentSuccess(merchantReference)
          }}
        />
      </div>
    )
  }

  return (
    <div style={{ padding: '24px', maxWidth: '800px', margin: '0 auto' }}>
      <div style={{ marginBottom: '24px' }}>
        <Button icon={<ArrowLeftOutlined />} onClick={onBack} style={{ marginBottom: '16px' }}>
          Back to Selection
        </Button>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>
          {consultationType === 'flexi' ? 'Flexi' : 'Priority'} Consultation
        </h1>
        <p style={{ color: '#8c8c8c' }}>
          {consultationType === 'flexi'
            ? 'Book a flexible consultation - a doctor will contact you during the day'
            : 'Book a priority consultation - a doctor will contact you within minutes'}
        </p>
      </div>

      {currentStep < 3 && (
        <Steps current={currentStep} style={{ marginBottom: '32px' }}>
          {steps.map((step, index) => (
            <Step key={index} title={step.title} description={step.description} />
          ))}
        </Steps>
      )}

      <Card style={{ marginBottom: '24px' }}>{renderStepContent()}</Card>

      {currentStep < 3 && (
        <div style={{ textAlign: 'right' }}>
          {currentStep > 0 && (
            <Button style={{ marginRight: '8px' }} onClick={handlePrevious}>
              Previous
            </Button>
          )}
          {currentStep < 2 ? (
            <Button type='primary' onClick={handleNext}>
              Next
            </Button>
          ) : (
            <Button type='primary' loading={loading} onClick={handleSubmit}>
              {getConsultationCost() > 0 && !paymentCompleted ? 'Proceed to Payment' : 'Confirm Booking'}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

export default FlexiPriorityBooking
